import type { App } from "@upsquad/server";
import type {
	CreateExchangeCodePayload,
	ExchangeCodePayload,
	GoogleOAuthCallbackPayload,
	MicrosoftOAuthCallbackPayload,
	RefreshTokenPayload,
	RequestOtpPayload,
	VerifyOtpPayload,
} from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

type RequestOtpResponse = App["~Routes"]["auth"]["request-otp"]["post"]["response"]["200"];
type VerifyOtpResponse = App["~Routes"]["auth"]["verify-otp"]["post"]["response"]["200"];
type GoogleAuthResponse = App["~Routes"]["auth"]["google"]["post"]["response"]["200"];
type MicrosoftAuthResponse = App["~Routes"]["auth"]["microsoft"]["post"]["response"]["200"];
type RefreshResponse = App["~Routes"]["auth"]["refresh"]["post"]["response"]["200"];
type LogoutResponse = App["~Routes"]["auth"]["logout"]["post"]["response"]["200"];
type CreateExchangeCodeResponse = App["~Routes"]["auth"]["create-exchange-code"]["post"]["response"]["200"];
type ExchangeCodeResponse = App["~Routes"]["auth"]["exchange-code"]["post"]["response"]["200"];

export const authApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		requestOtp: builder.mutation<RequestOtpResponse, RequestOtpPayload>({
			query:
				(payload): EdenCall<RequestOtpResponse> =>
				(client) =>
					client.auth["request-otp"].post(payload),
		}),
		verifyOtp: builder.mutation<VerifyOtpResponse, VerifyOtpPayload>({
			query:
				(payload): EdenCall<VerifyOtpResponse> =>
				(client) =>
					client.auth["verify-otp"].post(payload),
		}),
		googleAuth: builder.mutation<GoogleAuthResponse, GoogleOAuthCallbackPayload>({
			query:
				(payload): EdenCall<GoogleAuthResponse> =>
				(client) =>
					client.auth.google.post(payload),
		}),
		microsoftAuth: builder.mutation<MicrosoftAuthResponse, MicrosoftOAuthCallbackPayload>({
			query:
				(payload): EdenCall<MicrosoftAuthResponse> =>
				(client) =>
					client.auth.microsoft.post(payload),
		}),
		refresh: builder.mutation<RefreshResponse, RefreshTokenPayload>({
			query:
				(payload): EdenCall<RefreshResponse> =>
				(client) =>
					client.auth.refresh.post(payload),
		}),
		logout: builder.mutation<LogoutResponse, RefreshTokenPayload>({
			query:
				(payload): EdenCall<LogoutResponse> =>
				(client) =>
					client.auth.logout.post(payload ?? {}),
		}),
		createExchangeCode: builder.mutation<CreateExchangeCodeResponse, CreateExchangeCodePayload>({
			query:
				(payload): EdenCall<CreateExchangeCodeResponse> =>
				(client) =>
					client.auth["create-exchange-code"].post(payload),
		}),
		exchangeCode: builder.mutation<ExchangeCodeResponse, ExchangeCodePayload>({
			query:
				(payload): EdenCall<ExchangeCodeResponse> =>
				(client) =>
					client.auth["exchange-code"].post(payload),
		}),
	}),
});

export const {
	useRequestOtpMutation,
	useVerifyOtpMutation,
	useGoogleAuthMutation,
	useMicrosoftAuthMutation,
	useRefreshMutation,
	useLogoutMutation,
	useCreateExchangeCodeMutation,
	useExchangeCodeMutation,
} = authApi;
